import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../utils/api';
import { useTheme, useMediaQuery } from '@mui/material';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
  { value: 'marketing', label: 'Marketing' },
];

const UserManagement = () => {
  const { user } = useAuth();
  const { selectedLocation } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1 });

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff',
    phone: '',
    password: '',
  });
  const [pwdForm, setPwdForm] = useState({ newPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);

  const isAdmin = useMemo(() => {
    const r = String(user?.role || '').toLowerCase();
    return r === 'admin' || r === 'super_admin' || r === 'superadmin' || r === 'super';
  }, [user]);

  const loadUsers = async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      params.set('_t', Date.now()); // Cache buster
      const res = await api.get(`/users?${params.toString()}`);
      let list = [];
      if (res.data?.success) {
        list = res.data.data.users || [];
        setPagination(res.data.data.pagination || { totalItems: 0, totalPages: 1 });
      }

      // Ensure current user is present in the table
      if (user?.id && !list.some((u) => u.id === user.id)) {
        try {
          const me = await api.get('/users/me');
          const meData = me?.data?.data?.user;
          if (meData) {
            list = [
              {
                id: meData.id || meData._id,
                firstName: meData.firstName,
                lastName: meData.lastName,
                email: meData.email,
                role: meData.role,
                phone: meData.phone,
                isActive: meData.isActive !== false,
                createdAt: meData.createdAt,
              },
              ...list,
            ];
          }
        } catch (e) {
          // ignore if /users/me fails; table will still render
        }
      }

      setUsers(list);
    } catch (e) {
      console.error('Failed to load users', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const openAdd = () => {
    setForm({ firstName: '', lastName: '', email: '', role: 'staff', phone: '', password: '' });
    setAddOpen(true);
  };
  const openEdit = (u) => {
    setSelectedUser(u);
    setForm({ firstName: u.firstName || '', lastName: u.lastName || '', email: u.email || '', role: String(u.role || '').toLowerCase(), phone: u.phone || '' });
    setEditOpen(true);
  };
  const openPwd = (u) => {
    setSelectedUser(u);
    setPwdForm({ newPassword: '' });
    setPwdOpen(true);
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      const payload = { ...form, role: form.role };
      // Include the current location so the new user gets access to it
      if (selectedLocation?.id || selectedLocation?._id) {
        payload.locationId = selectedLocation.id || selectedLocation._id;
      }
      const res = await api.post('/users', payload);
      if (res.data?.success) {
        setAddOpen(false);
        await loadUsers();
      }
    } catch (e) {
      console.error('Create user failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      const payload = { ...form };
      delete payload.password; // not allowed here
      const res = await api.put(`/users/${selectedUser.id}`, payload);
      if (res.data?.success) {
        setEditOpen(false);
        await loadUsers();
      }
    } catch (e) {
      console.error('Update user failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      setLoading(true);
      const res = await api.put(`/users/${selectedUser.id}/password`, { newPassword: pwdForm.newPassword });
      if (res.data?.success) {
        setPwdOpen(false);
      }
    } catch (e) {
      console.error('Reset password failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (u) => {
    if (!window.confirm(`Reactivate ${u.firstName || ''} ${u.lastName || ''}?`)) return;
    
    try {
      setLoading(true);
      await api.put(`/users/${u.id}`, { isActive: true });
      // Always reload users after update
      await loadUsers();
    } catch (e) {
      console.error('Reactivate user failed', e);
      alert(`Failed to reactivate user: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (u, permanent = false, cascade = false) => {
    const action = permanent ? 'permanently delete' : 'deactivate';
    const warning = permanent 
      ? `PERMANENTLY DELETE ${u.firstName || ''} ${u.lastName || ''}?\n\nThis action CANNOT be undone. The user and all their data will be removed.`
      : `Deactivate ${u.firstName || ''} ${u.lastName || ''}?\n\nThe account will be disabled but can be reactivated later.`;
    
    if (!cascade && !window.confirm(warning)) return;
    
    try {
      setLoading(true);
      let url = `/users/${u.id}`;
      if (permanent) url += '?permanent=true';
      if (cascade) url += permanent ? '&cascade=true' : '?cascade=true';
      
      const res = await api.delete(url);
      if (res.data?.success) {
        await loadUsers();
      }
    } catch (e) {
      console.error(`${action} user failed`, e);
      
      // Check if server is asking for cascade confirmation
      if (e.response?.status === 409 && e.response?.data?.requiresCascade) {
        const data = e.response.data;
        const cascadeWarning = `⚠️ WARNING: ${data.message}\n\nThis will permanently delete:\n` +
          `• ${data.relatedRecords.locations || 0} location(s)\n` +
          `• ${data.relatedRecords.orders || 0} order(s)\n` +
          `• ${data.relatedRecords.inventory || 0} inventory item(s)\n` +
          `• ${data.relatedRecords.customers || 0} customer(s)\n\n` +
          `Are you SURE you want to delete everything?`;
        
        if (window.confirm(cascadeWarning)) {
          // Retry with cascade=true
          await handleDelete(u, true, true);
        }
        return;
      }
      
      alert(`Failed to ${action} user: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Current user summary */}
      {user && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Logged in as</Typography>
            <Typography variant="h6">{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}</Typography>
            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={(String(user.role || '')).toLowerCase()} color="primary" variant="outlined" />
            <Chip label={user.isActive === false ? 'inactive' : 'active'} color={user.isActive === false ? 'default' : 'success'} variant="outlined" />
          </Box>
        </Paper>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>User Management</Typography>
        <Box>
          <TextField
            size="small"
            placeholder="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadUsers(); } }}
            sx={{ mr: 1.5, width: 260 }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={!isAdmin}>
            Add User
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    {user?.id && u.id === user.id ? ' (You)' : ''}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{String(u.role || '').toLowerCase()}</TableCell>
                  <TableCell>{u.isActive ? 'active' : 'inactive'}</TableCell>
                  <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" onClick={() => openEdit(u)} disabled={!isAdmin}><EditIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <span>
                        <IconButton size="small" onClick={() => openPwd(u)} disabled={!isAdmin || (user?.id && u.id === user.id)}><LockResetIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                    {!u.isActive && (
                      <Tooltip title="Reactivate">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleReactivate(u)} 
                            disabled={!isAdmin}
                            color="success"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete Options">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            setDeleteMenuAnchor(e.currentTarget);
                            setDeleteTargetUser(u);
                          }} 
                          disabled={!isAdmin || (user?.id && u.id === user.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {loading ? <CircularProgress size={24} /> : 'No users found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Options Menu */}
      <Menu
        anchorEl={deleteMenuAnchor}
        open={Boolean(deleteMenuAnchor)}
        onClose={() => {
          setDeleteMenuAnchor(null);
          setDeleteTargetUser(null);
        }}
      >
        <MenuItem 
          onClick={() => {
            setDeleteMenuAnchor(null);
            if (deleteTargetUser) handleDelete(deleteTargetUser, false);
            setDeleteTargetUser(null);
          }}
        >
          <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Deactivate" secondary="Disable account (can reactivate)" />
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            setDeleteMenuAnchor(null);
            if (deleteTargetUser) handleDelete(deleteTargetUser, true);
            setDeleteTargetUser(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText 
            primary="Permanently Delete" 
            secondary="Cannot be undone"
            primaryTypographyProps={{ color: 'error' }}
          />
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" fullWidth value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" fullWidth value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" type="email" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Role" fullWidth value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Temporary Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={loading}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" fullWidth value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" fullWidth value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" type="email" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Role" fullWidth value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            value={pwdForm.newPassword}
            onChange={(e) => setPwdForm({ newPassword: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={loading}>Update</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UserManagement;
